(() => {
  const STORAGE_KEY = 'soundhousCompareV1';
  const MAX_PRODUCTS = 4;

  const FAMILY_SCHEMAS = {
    soundbar: [
      {
        label: 'Which is right for you?',
        rows: [
          ['decision.best_for', 'Best for'],
          ['decision.room_fit', 'Room fit'],
          ['decision.tv_fit', 'TV fit'],
          ['decision.sound_profile', 'Sound profile'],
          ['decision.key_advantage', 'Key advantage'],
          ['decision.key_compromise', 'Key compromise'],
          ['decision.soundhous_recommendation', 'Soundhous recommendation']
        ]
      },
      {
        label: 'Experience',
        rows: [
          ['experience.dolby_atmos', 'Dolby Atmos'],
          ['experience.height_channels', 'Height channels'],
          ['experience.bluetooth_audio', 'Bluetooth audio'],
          ['experience.voice_control', 'Voice control'],
          ['experience.trueplay', 'Trueplay'],
          ['experience.speech_enhancement', 'Speech Enhancement'],
          ['experience.night_sound', 'Night Sound']
        ]
      },
      {
        label: 'Acoustic architecture',
        rows: [
          ['acoustics.amplifiers', 'Amplifiers'],
          ['acoustics.tweeters', 'Tweeters'],
          ['acoustics.midwoofers', 'Midwoofers'],
          ['acoustics.bass_architecture', 'Bass architecture']
        ]
      },
      {
        label: 'Connectivity',
        rows: [
          ['connectivity.hdmi_earc', 'HDMI eARC'],
          ['connectivity.wifi', 'WiFi'],
          ['connectivity.airplay_2', 'Apple AirPlay 2'],
          ['connectivity.ethernet', 'Ethernet']
        ]
      },
      {
        label: 'Physical',
        rows: [
          ['physical.width', 'Width'],
          ['physical.height', 'Height'],
          ['physical.depth', 'Depth'],
          ['physical.weight', 'Weight'],
          ['physical.colours', 'Colours']
        ]
      }
    ],
    wireless_home_speaker: [
      {
        label: 'Which is right for you?',
        rows: [
          ['decision.best_for', 'Best for'],
          ['decision.room_fit', 'Room fit'],
          ['decision.listening_profile', 'Listening profile'],
          ['decision.spatial_audio', 'Spatial audio'],
          ['decision.stereo_pairing', 'Stereo pairing'],
          ['decision.home_theatre_surround', 'Home theatre surround use'],
          ['decision.humidity_use', 'Humid-space use'],
          ['decision.key_advantage', 'Key advantage'],
          ['decision.key_compromise', 'Key compromise'],
          ['decision.soundhous_recommendation', 'Soundhous recommendation']
        ]
      },
      {
        label: 'Experience',
        rows: [
          ['experience.dolby_atmos_music', 'Dolby Atmos Music'],
          ['experience.bluetooth_audio', 'Bluetooth audio'],
          ['experience.voice_control', 'Voice control'],
          ['experience.trueplay', 'Trueplay'],
          ['experience.line_in', 'Line-in'],
          ['experience.airplay_2', 'Apple AirPlay 2']
        ]
      },
      {
        label: 'Acoustic architecture',
        rows: [
          ['acoustics.amplifiers', 'Amplifiers'],
          ['acoustics.tweeters', 'Tweeters'],
          ['acoustics.woofers', 'Woofers'],
          ['acoustics.architecture', 'Architecture']
        ]
      },
      {
        label: 'Connectivity',
        rows: [
          ['connectivity.wifi', 'WiFi'],
          ['connectivity.bluetooth', 'Bluetooth'],
          ['connectivity.ethernet', 'Ethernet']
        ]
      },
      {
        label: 'Physical',
        rows: [
          ['physical.width', 'Width'],
          ['physical.height', 'Height'],
          ['physical.depth', 'Depth'],
          ['physical.weight', 'Weight'],
          ['physical.colours', 'Colours'],
          ['physical.power_source', 'Power source']
        ]
      }
    ]
  };

  const escapeHtml = (value) => String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const getPath = (object, path) => path.split('.').reduce((value, key) => (
    value && Object.prototype.hasOwnProperty.call(value, key) ? value[key] : null
  ), object);

  const normalize = (value) => String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

  const extractProductHandle = (url) => {
    try {
      const pathname = new URL(url, window.location.origin).pathname;
      const match = pathname.match(/\/products\/([^/?#]+)/i);
      return match ? decodeURIComponent(match[1]).toLowerCase() : null;
    } catch (error) {
      const match = String(url || '').match(/\/products\/([^/?#]+)/i);
      return match ? decodeURIComponent(match[1]).toLowerCase() : null;
    }
  };

  const loadSelection = () => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(parsed) ? parsed.filter((item) => item && item.handle && item.profile) : [];
    } catch (error) {
      return [];
    }
  };

  const saveSelection = (selection) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(selection));
    } catch (error) {
      // Continue in-memory if storage is unavailable.
    }
  };

  const initRoot = (root) => {
    if (!root || root.dataset.shcmpReady === 'true') return;
    root.dataset.shcmpReady = 'true';

    const registryNode = root.querySelector('[data-shcmp-registry]');
    if (!registryNode) return;

    let registry = [];
    try {
      registry = JSON.parse(registryNode.textContent || '[]');
    } catch (error) {
      console.error('[Soundhous Compare] Invalid registry JSON.', error);
      return;
    }

    const byHandle = new Map(registry.map((product) => [String(product.handle).toLowerCase(), product]));
    const bySourceHandle = new Map();
    registry.forEach((product) => {
      const sourceHandle = String(product.sourceHandle || product.handle || '').toLowerCase();
      if (!sourceHandle) return;
      const items = bySourceHandle.get(sourceHandle) || [];
      items.push(product);
      bySourceHandle.set(sourceHandle, items);
    });

    const tray = root.querySelector('[data-shcmp-tray]');
    const countNode = root.querySelector('[data-shcmp-count]');
    const statusNode = root.querySelector('[data-shcmp-status]');
    const openButton = root.querySelector('[data-shcmp-open]');
    const clearButton = root.querySelector('[data-shcmp-clear]');
    const modal = root.querySelector('[data-shcmp-modal]');
    const modalPanel = root.querySelector('.shcmp-modal__panel');
    const modalHeader = root.querySelector('.shcmp-modal__header');
    const closeButton = root.querySelector('[data-shcmp-close]');
    const differencesToggle = root.querySelector('[data-shcmp-differences]');
    const stickyTarget = root.querySelector('[data-shcmp-sticky]');
    const tableTarget = root.querySelector('[data-shcmp-table]');

    let selection = loadSelection().slice(0, MAX_PRODUCTS);
    selection = selection
      .map((saved) => byHandle.get(String(saved.handle).toLowerCase()) || saved)
      .filter((saved) => saved && saved.profile);
    saveSelection(selection);

    let horizontalSyncLock = false;

    const setStatus = (message = '') => {
      if (statusNode) statusNode.textContent = message;
    };

    const selectedHandles = () => new Set(selection.map((product) => String(product.handle).toLowerCase()));

    const syncButtons = () => {
      const selected = selectedHandles();
      document.querySelectorAll('[data-shcmp-handle]').forEach((button) => {
        const active = selected.has(String(button.dataset.shcmpHandle).toLowerCase());
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
        const box = button.querySelector('.shcmp-add__box');
        const label = button.querySelector('.shcmp-add__label');
        if (box) box.textContent = active ? '✓' : '';
        if (label) label.textContent = button.dataset.shcmpLabel || 'Compare';
      });
    };

    const syncTray = () => {
      if (!tray || !countNode || !openButton) return;
      tray.hidden = selection.length === 0;
      countNode.textContent = `${selection.length} ${selection.length === 1 ? 'product' : 'products'}`;
      openButton.disabled = selection.length < 2;
      syncButtons();
    };

    const hideStickyHeader = () => {
      if (!stickyTarget) return;
      stickyTarget.hidden = true;
      stickyTarget.setAttribute('aria-hidden', 'true');
    };

    const closeModal = () => {
      if (!modal) return;
      modal.hidden = true;
      document.body.classList.remove('shcmp-modal-open');
      if (differencesToggle) differencesToggle.checked = false;
      hideStickyHeader();
      openButton?.focus();
    };

    const toggleProduct = (product) => {
      const existingIndex = selection.findIndex((item) => item.handle === product.handle);

      if (existingIndex >= 0) {
        selection.splice(existingIndex, 1);
        setStatus('Removed from comparison.');
      } else {
        const currentFamily = selection[0]?.profile?.family;
        const incomingFamily = product.profile?.family;

        if (currentFamily && incomingFamily && currentFamily !== incomingFamily) {
          setStatus('Compare products from the same product family.');
          syncTray();
          return;
        }

        if (selection.length >= MAX_PRODUCTS) {
          setStatus(`You can compare up to ${MAX_PRODUCTS} products at once.`);
          syncTray();
          return;
        }

        selection.push(product);
        setStatus('Added to comparison.');
      }

      saveSelection(selection);
      syncTray();

      if (modal && !modal.hidden && selection.length >= 2) {
        renderTable();
        window.requestAnimationFrame(updateStickyHeader);
      }
      if (modal && !modal.hidden && selection.length < 2) closeModal();
    };

    const productsForCard = (card) => {
      const links = [...card.querySelectorAll('a[href*="/products/"]')];
      for (const link of links) {
        const handle = extractProductHandle(link.getAttribute('href') || link.href);
        if (!handle) continue;
        const products = bySourceHandle.get(handle);
        if (products?.length) return products;
      }
      return [];
    };

    const buildCompareButton = (product) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'shcmp-add';
      button.dataset.shcmpHandle = product.handle;
      button.dataset.shcmpLabel = product.shortLabel || 'Compare';
      button.setAttribute('aria-pressed', 'false');
      button.setAttribute('aria-label', `Compare ${product.title}`);
      button.innerHTML = `<span class="shcmp-add__box" aria-hidden="true"></span><span class="shcmp-add__label">${escapeHtml(product.shortLabel || 'Compare')}</span>`;

      button.addEventListener('pointerdown', (event) => event.stopPropagation());
      button.addEventListener('mousedown', (event) => event.stopPropagation());
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        toggleProduct(product);
        const active = button.getAttribute('aria-pressed') === 'true';
        button.setAttribute('aria-label', `${active ? 'Remove' : 'Compare'} ${product.title}`);
      });
      return button;
    };

    const injectButtons = () => {
      document.querySelectorAll('.product-card-wrapper, .card-wrapper').forEach((card) => {
        if (card.dataset.shcmpControlsReady === 'true') return;

        const products = productsForCard(card);
        if (!products.length) return;

        card.dataset.shcmpControlsReady = 'true';

        if (products.length === 1) {
          card.appendChild(buildCompareButton(products[0]));
          return;
        }

        const group = document.createElement('div');
        group.className = 'shcmp-add-group';
        group.setAttribute('aria-label', 'Choose In-Ceiling size to compare');

        const title = document.createElement('span');
        title.className = 'shcmp-add-group__title';
        title.textContent = 'Compare size';
        group.appendChild(title);

        products.forEach((product) => group.appendChild(buildCompareButton(product)));
        card.appendChild(group);
      });

      syncButtons();
    };

    const stickyGridMarkup = (minWidth, firstColumnWidth, productColumnWidth) => {
      const productCards = selection.map((product) => `
        <div class="shcmp-sticky-product" data-shcmp-product-handle="${escapeHtml(product.handle)}">
          <div class="shcmp-sticky-product__identity">
            <strong class="shcmp-sticky-product__name">${escapeHtml(product.title)}</strong>
            ${product.shortLabel && product.shortLabel !== 'Compare' ? `<span class="shcmp-variant-badge">${escapeHtml(product.shortLabel)}</span>` : ''}
          </div>
          <div class="shcmp-sticky-product__image">
            ${product.image ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" loading="eager">` : ''}
          </div>
          <p class="shcmp-sticky-product__price">${escapeHtml(product.price)}</p>
          <div class="shcmp-sticky-product__actions">
            <a class="shcmp-view" href="${escapeHtml(product.url)}">View details</a>
            <button type="button" class="shcmp-remove" data-shcmp-remove="${escapeHtml(product.handle)}">Remove</button>
          </div>
        </div>
      `).join('');

      return `
        <div
          class="shcmp-sticky-grid"
          style="min-width:${minWidth}px;grid-template-columns:${firstColumnWidth}px repeat(${selection.length}, minmax(${productColumnWidth}px, 1fr));"
        >
          <div class="shcmp-sticky-label">Feature</div>
          ${productCards}
        </div>
      `;
    };

    const renderStickyHeader = (minWidth, firstColumnWidth, productColumnWidth) => {
      if (!stickyTarget) return;
      stickyTarget.innerHTML = stickyGridMarkup(minWidth, firstColumnWidth, productColumnWidth);
      stickyTarget.scrollLeft = tableTarget?.scrollLeft || 0;
      stickyTarget.querySelectorAll('[data-shcmp-remove]').forEach((button) => {
        button.addEventListener('click', () => {
          const product = selection.find((item) => item.handle === button.dataset.shcmpRemove);
          if (product) toggleProduct(product);
        });
      });
    };

    const updateStickyHeader = () => {
      if (!modal || modal.hidden || !stickyTarget || !modalHeader || !modalPanel || !tableTarget) return;

      const showcase = tableTarget.querySelector('[data-shcmp-showcase]');
      if (!showcase) {
        hideStickyHeader();
        return;
      }

      const headerRect = modalHeader.getBoundingClientRect();
      const showcaseRect = showcase.getBoundingClientRect();
      const panelRect = modalPanel.getBoundingClientRect();
      const shouldShow = showcaseRect.top <= headerRect.bottom + 4;

      if (!shouldShow) {
        hideStickyHeader();
        return;
      }

      stickyTarget.hidden = false;
      stickyTarget.setAttribute('aria-hidden', 'false');
      stickyTarget.style.top = `${Math.round(headerRect.bottom)}px`;
      stickyTarget.style.left = `${Math.round(Math.max(panelRect.left, 0))}px`;
      stickyTarget.style.width = `${Math.round(Math.min(panelRect.width, window.innerWidth))}px`;
      stickyTarget.scrollLeft = tableTarget.scrollLeft;
    };

    const renderTable = () => {
      if (!tableTarget || selection.length < 2) return;

      const family = selection[0]?.profile?.family;
      const schema = FAMILY_SCHEMAS[family] || [];
      const onlyDifferences = Boolean(differencesToggle?.checked);
      const firstColumnWidth = window.matchMedia('(max-width: 749px)').matches ? 118 : 220;
      const productColumnWidth = window.matchMedia('(max-width: 749px)').matches ? 198 : 290;
      const minWidth = firstColumnWidth + (selection.length * productColumnWidth);

      const headerCells = selection.map((product) => `
        <th scope="col" class="shcmp-product-name">
          <div class="shcmp-product-name__identity">
            <span class="shcmp-product-name__title">${escapeHtml(product.title)}</span>
            ${product.shortLabel && product.shortLabel !== 'Compare' ? `<span class="shcmp-variant-badge">${escapeHtml(product.shortLabel)}</span>` : ''}
          </div>
        </th>
      `).join('');

      const showcaseCells = selection.map((product) => `
        <td class="shcmp-product-showcase" data-shcmp-product-handle="${escapeHtml(product.handle)}">
          <div class="shcmp-product-showcase__image">
            ${product.image ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" loading="eager">` : ''}
          </div>
          <p class="shcmp-product-showcase__price">${escapeHtml(product.price)}</p>
          <div class="shcmp-product-showcase__actions">
            <a class="shcmp-view" href="${escapeHtml(product.url)}">View details</a>
            <button type="button" class="shcmp-remove" data-shcmp-remove="${escapeHtml(product.handle)}">Remove</button>
          </div>
        </td>
      `).join('');

      const body = schema.map((group, groupIndex) => {
        const rowMarkup = group.rows.map(([path, label]) => {
          const values = selection.map((product) => getPath(product.profile, path));
          const normalized = values.map(normalize).filter(Boolean);
          const unique = new Set(normalized);
          const hasAnyValue = normalized.length > 0;
          const isDifferent = unique.size > 1 || normalized.length !== values.length;

          if (!hasAnyValue || (onlyDifferences && !isDifferent)) return '';

          const rowClasses = [
            'shcmp-data-row',
            path.startsWith('decision.') ? 'is-guidance' : '',
            path === 'decision.soundhous_recommendation' ? 'is-recommendation' : ''
          ].filter(Boolean).join(' ');

          const valueCells = values.map((value) => `
            <td class="shcmp-value">${escapeHtml(value ?? '—')}</td>
          `).join('');

          return `
            <tr class="${rowClasses}" data-different="${isDifferent ? 'true' : 'false'}">
              <th scope="row" class="shcmp-label">${escapeHtml(label)}</th>
              ${valueCells}
            </tr>
          `;
        }).join('');

        if (!rowMarkup) return '';

        return `
          <tr class="shcmp-group-row${groupIndex === 0 ? ' shcmp-group-row--guidance' : ''}">
            <th colspan="${selection.length + 1}">${escapeHtml(group.label)}</th>
          </tr>
          ${rowMarkup}
        `;
      }).join('');

      tableTarget.innerHTML = `
        <table class="shcmp-table" style="min-width:${minWidth}px">
          <thead>
            <tr>
              <th scope="col" class="shcmp-feature-head">Feature</th>
              ${headerCells}
            </tr>
          </thead>
          <tbody>
            <tr class="shcmp-showcase-row" data-shcmp-showcase>
              <th scope="row" class="shcmp-showcase-label"><span class="visually-hidden">Products</span></th>
              ${showcaseCells}
            </tr>
            ${body}
          </tbody>
        </table>
      `;

      renderStickyHeader(minWidth, firstColumnWidth, productColumnWidth);

      tableTarget.querySelectorAll('[data-shcmp-remove]').forEach((button) => {
        button.addEventListener('click', () => {
          const product = selection.find((item) => item.handle === button.dataset.shcmpRemove);
          if (product) toggleProduct(product);
        });
      });
    };

    const openModal = () => {
      if (!modal || selection.length < 2) {
        setStatus('Choose at least two products to compare.');
        return;
      }

      renderTable();
      modal.hidden = false;
      document.body.classList.add('shcmp-modal-open');
      closeButton?.focus();

      window.requestAnimationFrame(() => {
        if (modal) modal.scrollTop = 0;
        hideStickyHeader();
      });
    };

    const clearSelection = () => {
      selection = [];
      saveSelection(selection);
      setStatus('');
      syncTray();
      closeModal();
    };

    openButton?.addEventListener('click', openModal);
    clearButton?.addEventListener('click', clearSelection);
    closeButton?.addEventListener('click', closeModal);
    differencesToggle?.addEventListener('change', () => {
      renderTable();
      window.requestAnimationFrame(updateStickyHeader);
    });

    modal?.addEventListener('click', (event) => {
      if (event.target === modal) closeModal();
    });

    modal?.addEventListener('scroll', updateStickyHeader, { passive: true });

    tableTarget?.addEventListener('scroll', () => {
      if (!stickyTarget || horizontalSyncLock) return;
      horizontalSyncLock = true;
      stickyTarget.scrollLeft = tableTarget.scrollLeft;
      horizontalSyncLock = false;
    }, { passive: true });

    stickyTarget?.addEventListener('scroll', () => {
      if (!tableTarget || horizontalSyncLock) return;
      horizontalSyncLock = true;
      tableTarget.scrollLeft = stickyTarget.scrollLeft;
      horizontalSyncLock = false;
    }, { passive: true });

    window.addEventListener('resize', updateStickyHeader, { passive: true });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && modal && !modal.hidden) closeModal();
    });

    injectButtons();
    syncTray();

    // Bear remains installed as a rollback reference during staged Native Compare rollout.
    const suppressLegacyCompare = () => {
      document.querySelectorAll('sc-dynamic-compare, sc-add-to-compare').forEach((element) => {
        element.style.setProperty('display', 'none', 'important');
        element.setAttribute('aria-hidden', 'true');
      });

      document.querySelectorAll('.product-card-wrapper, .card-wrapper').forEach((card) => {
        card.querySelectorAll('button, label, a, span').forEach((element) => {
          if (element.closest('[data-soundhous-compare-root]') || element.closest('[data-shcmp-handle]')) return;
          if ((element.textContent || '').trim().toLowerCase() === 'add to compare') {
            const legacyRoot = element.closest('sc-add-to-compare') || element;
            legacyRoot.style.setProperty('display', 'none', 'important');
            legacyRoot.setAttribute('aria-hidden', 'true');
          }
        });
      });
    };

    suppressLegacyCompare();

    let suppressionQueued = false;
    const observer = new MutationObserver(() => {
      if (suppressionQueued) return;
      suppressionQueued = true;
      window.requestAnimationFrame(() => {
        suppressionQueued = false;
        suppressLegacyCompare();
        injectButtons();
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
    window.setTimeout(() => observer.disconnect(), 10000);
  };

  const init = () => {
    document.querySelectorAll('[data-soundhous-compare-root]').forEach(initRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
